import { AppForm } from '../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { Injectable, Signal } from '@angular/core';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationAppEntityApplicationWrite,
  ParcelAppEntityParcelWrite,
  PropertyLegalTitleAppEntityDictionaryPropertyLegalTitle,
  PropertyLegalTitleService
} from '../../../../../../openapi';
import { toSignal } from '@angular/core/rxjs-interop';
import { DictionaryCommonService } from '../../../../../../services/dictionary.common.service';
import {
  DictTownRoadItem,
  DictTownStreetItem,
  DictTownWithRoadsItem,
  DictTownWithStreetsItem
} from '../../../../../../services/dictionary.common.model';

export type PurposeOfUseItem = 'Przemysłowy' | 'Usługowy' | 'Mieszkaniowy' | 'Rolniczy' | 'Inny' | 'Leśny' | '';

export type PostInvestmentUsageItem
  = 'Nie ulegnie zmianie'
  | 'Ulegnie zmianie na mieszkaniowy'
  | 'Ulegnie zmianie na inny cel'
  | string;

@Injectable()
export class LocationDataForm extends AppForm {

  public purposeOfUseList: PurposeOfUseItem[] = [
    'Przemysłowy',
    'Usługowy',
    'Mieszkaniowy',
    'Rolniczy',
    'Leśny',
    'Inny'
  ];

  public postInvestmentUsages: PostInvestmentUsageItem[] = [
    'Nie ulegnie zmianie',
    'Ulegnie zmianie na mieszkaniowy',
    'Ulegnie zmianie na inny cel'
  ];

  public propertyLegalTitles: Signal<PropertyLegalTitleAppEntityDictionaryPropertyLegalTitle[] | undefined> = toSignal(this.pr.getPropertyLegalTitleCollectionPropertyLegalTitleCollection({ pagination: false }));

  public townWithStreetsList: Signal<DictTownWithStreetsItem[] | undefined> = toSignal(this.dictService.getTownWithStreetsList());

  public townWithRoadsList: Signal<DictTownWithRoadsItem[] | undefined> = toSignal(this.dictService.getTownWithRoadsList());

  public getRoadList(townName?: string | null): Array<DictTownRoadItem> {
    return this.townWithRoadsList()?.filter(v => v.name === townName).at(0)?.roads || [];
  }

  public getStreetList(townName?: string | null): Array<DictTownStreetItem> {
    return this.townWithStreetsList()?.filter(v => v.name === townName)?.at(0)?.streets || [];
  }

  constructor(
    private pr: PropertyLegalTitleService,
    private dictService: DictionaryCommonService
  ) {
    super({
      city: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
      provideRoadNumberAndName: new FormControl(true),

      propertyLegalTitle: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      purposeOfUse: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      parcels: new FormControl<ParcelAppEntityParcelWrite[]>(
        [], {
          nonNullable: true,
          validators: [Validators.required, Validators.minLength(1), Validators.maxLength(255)]
        }
      )
    });
  }

  public get provideRoadNumberAndName(): FormControl {
    return this.get('provideRoadNumberAndName') as FormControl;
  }

  public get city(): FormControl {
    return this.get('city') as FormControl;
  }

  public cityChange(v?: string | null): void {
    this.street?.setValue('');
    this.street?.markAsUntouched();
    this.roadNumber?.setValue('');
    this.roadNumber?.markAsUntouched();
  }

  public provideRoadNumberAndNameChange(v?: boolean): void {
    this.city.setValue('');
    this.city.markAsUntouched();
    if (v) {
      this.addControl('roadNumber', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));

      this.addControl('roadName', new FormControl({
        disabled: true,
        value: null
      }, [Validators.required, Validators.maxLength(255)]));

      this.removeControl('street');
    } else {
      this.removeControl('roadName');
      this.removeControl('roadNumber');
      this.addControl('street', new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }));
    }
  }

  public get roadName(): FormControl | null {
    return this.get('roadName') as FormControl | null;
  }

  public get roadNumber(): FormControl | null {
    return this.get('roadNumber') as FormControl | null;
  }

  public roadNumberChange(roadNumber: string): void {
    const roadName = this.getRoadList(this.city.value).filter(v => v.roadNumber === roadNumber).at(0)?.roadName || '';
    this.roadName?.setValue(roadName);
  }

  public get street(): FormControl | null {
    return this.get('street') as FormControl | null;
  }

  public propertyLegalTitleChange(v: string): void {
    if (v === 'be22eabc-2332-4b22-af5b-4e8af2500983') {
      this.addControl('propertyLegalTitleDesc', new FormControl(null, [Validators.required, Validators.maxLength(255)]));
    } else {
      this.removeControl('propertyLegalTitleDesc');
    }
  }

  public get propertyLegalTitleDesc(): FormControl | null {
    return this.get('propertyLegalTitleDesc') as FormControl | null;
  }

  public get parcels(): FormControl {
    return this.get('parcels') as FormControl;
  }

  public get parcelsValues(): ParcelAppEntityParcelWrite[] {
    return [...this.parcels.value];
  }

  public createPostInvestmentUsageControl(): void {
    this.addControl('postInvestmentUsage', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get postInvestmentUsage(): FormControl | null {
    return this.get('postInvestmentUsage') as FormControl | null;
  }

  public createReferenceVehicleTypeControl(): void {
    this.addControl('referenceVehicleType', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }));
  }

  public get referenceVehicleType(): FormControl | null {
    return this.get('referenceVehicleType') as FormControl | null;
  }

  public createPropertyParkingSpacesCountControl(): void {
    this.addControl('propertyParkingSpacesCount', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get propertyParkingSpacesCount(): FormControl | null {
    return this.get('propertyParkingSpacesCount') as FormControl | null;
  }

  public createDecisionControls(): void {
    this.addControl('decisionNumber', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
    this.addControl('decisionDate', new FormControl(null, { validators: [Validators.required, Validators.maxLength(255)] }));
  }

  public get decisionNumber(): FormControl | null {
    return this.get('decisionNumber') as FormControl | null;
  }

  public get decisionDate(): FormControl | null {
    return this.get('decisionDate') as FormControl | null;
  }

  public purposeOfUseChange(v: PurposeOfUseItem | string): void {
    if (v === 'Inny') {
      this.addControl('purposeOfUseDesc', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('purposeOfUseDesc');
    }
  }

  public postInvestmentUsageChange(v: PostInvestmentUsageItem): void {
    if (v?.includes('Ulegnie zmianie na inny cel')) {
      this.addControl('propertyUsageChange', new FormControl('', [Validators.required, Validators.maxLength(5000)]));
    } else {
      this.removeControl('propertyUsageChange');
    }
  }

  public override patchFormValues(app: ApplicationAppEntityApplicationRead): void {
    this.purposeOfUseChange(app.propertyUsagePurpose as PurposeOfUseItem);
    this.postInvestmentUsageChange(app.postInvestmentUsage as PostInvestmentUsageItem);
    this.provideRoadNumberAndNameChange(app?.provideRoadNumberAndName);

    super.patchFormValues({
      provideRoadNumberAndName: !!app?.provideRoadNumberAndName,
      roadName: app?.roadName || '',
      roadNumber: app?.roadNumber || '',
      city: app?.city || '',
      street: app?.street || '',
      precinct: app.precint,
      registrationNumber: app.registrationNumber,
      propertyLegalTitle: app.propertyLegalTitle?.id || '',
      propertyLegalTitleDesc: app?.propertyLegalTitleDesc || '',
      purposeOfUse: app.propertyUsagePurpose || '',
      postInvestmentUsage: app?.postInvestmentUsage || '',
      propertyUsageChange: app?.propertyUsageChange || '',
      decisionDate: app?.decisionDate,
      decisionNumber: app?.decisionNumber,
      referenceVehicleType: app?.referenceVehicleType || '',
      propertyParkingSpacesCount: app?.propertyParkingSpacesCount || ''
    });

    if (app?.propertyUsagePurposeText) {
      this.patchValue({ purposeOfUseDesc: app.propertyUsagePurposeText });
    }

    if (app?.parcels?.length) {
      this.parcels.setValue(app.parcels.filter(v => v.tag === 'parcels'));
    }
  }

  public toWriteModel(): ApplicationAppEntityApplicationWrite {
    return {
      provideRoadNumberAndName: this.value.provideRoadNumberAndName,
      roadName: this.roadName?.value || null,
      roadNumber: this.value?.roadNumber || null,
      city: this.value?.city,
      street: this.street?.value || null,
      precint: this.value?.precinct,
      registrationNumber: this.value.registrationNumber,
      propertyUsagePurpose: this.value.purposeOfUse,
      propertyUsagePurposeText: this.value?.purposeOfUseDesc || undefined,
      propertyLegalTitle: this.value?.propertyLegalTitle,
      propertyLegalTitleDesc: this.value?.propertyLegalTitleDesc,
      postInvestmentUsage: this.value?.postInvestmentUsage,
      propertyUsageChange: this.value?.propertyUsageChange,
      decisionDate: this.value?.decisionDate,
      decisionNumber: this.value?.decisionNumber,
      referenceVehicleType: this.value?.referenceVehicleType,
      propertyParkingSpacesCount: this.value?.propertyParkingSpacesCount ? +this.value?.propertyParkingSpacesCount : undefined
    };
  }
}
